import React, { useCallback, useEffect, useState } from 'react';
import { cancelarRun, fetchRun, listarRuns, renombrarRun } from './api';
import DetalleCorrida from './DetalleCorrida';
import EtiquetaEditable from './EtiquetaEditable';
import { corridaEnCurso, formatearUsd } from './metricas';
import { useRefrescoFirmas } from './useRefrescoFirmas';
import type { Caso, ConfigAnalisis, PlantillaCorrida, RunDetalle, RunResumen, VeredictoHumano } from './tipos';
import { Aviso, ChipStatus, btnSecundario, formatearFecha } from './ui';

/** Array estable para cuando no hay detalle: un `[]` nuevo por render haría resincronizar el hook cada vez. */
const SIN_CASOS: Caso[] = [];

/** Salto desde Casos: `n` cambia en cada salto para que abrir la MISMA corrida dos veces vuelva a disparar. */
export interface SaltoACorrida {
  runId: string;
  n: number;
}

/**
 * Sub-vista Corridas: lista + detalle con poll cada 5 s mientras la corrida esté en
 * curso. El poll refresca `run` y `resultados` pero CONSERVA `casos`: sus URLs firmadas
 * cambian en cada respuesta y reemplazarlas haría que CachedImg vuelva a bajar todas
 * las imágenes cada 5 s. Las URLs vencen a la hora: useRefrescoFirmas las renueva antes
 * (una vez por hora, esté o no en curso), reemplazando SOLO `casos`.
 */
const AnalisisCorridas: React.FC<{
  activa: boolean;
  config: ConfigAnalisis;
  salto: SaltoACorrida | null;
  onReutilizar: (plantilla: PlantillaCorrida) => void;
}> = ({ activa, config, salto, onReutilizar }) => {
  const [runs, setRuns] = useState<RunResumen[]>([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [errorLista, setErrorLista] = useState<string | null>(null);
  const [activaId, setActivaId] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<RunDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const recargarLista = useCallback(async () => {
    setCargandoLista(true);
    try {
      setRuns(await listarRuns());
      setErrorLista(null);
    } catch (e) {
      console.error('[analisis] listar corridas falló:', e);
      setErrorLista(e instanceof Error ? e.message : String(e));
    } finally {
      setCargandoLista(false);
    }
  }, []);

  useEffect(() => {
    if (activa) void recargarLista();
  }, [activa, recargarLista]);

  useEffect(() => {
    if (salto) setActivaId(salto.runId);
  }, [salto]);

  // Apertura (o reapertura) de una corrida: detalle completo con URLs frescas.
  useEffect(() => {
    if (!activaId) {
      setDetalle(null);
      return;
    }
    let vivo = true;
    setCargandoDetalle(true);
    setErrorDetalle(null);
    setDetalle(null);
    fetchRun(activaId)
      .then((d) => {
        if (!vivo) return;
        setDetalle(d);
        setRuns((prev) => (prev.some((r) => r.run_id === d.run.run_id) ? prev.map((r) => (r.run_id === d.run.run_id ? d.run : r)) : [d.run, ...prev]));
      })
      .catch((e) => {
        console.error('[analisis] detalle de corrida falló:', e);
        if (vivo) setErrorDetalle(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (vivo) setCargandoDetalle(false);
      });
    return () => {
      vivo = false;
    };
  }, [activaId]);

  // Poll cada 5 s mientras la corrida activa esté en curso. Un poll que falla loguea,
  // avisa en pantalla y sigue intentando (tick): morir en silencio deja el detalle clavado.
  useEffect(() => {
    if (!activaId || !detalle || !corridaEnCurso(detalle.run)) return;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const d = await fetchRun(activaId);
          setDetalle((prev) =>
            prev && prev.run.run_id === d.run.run_id
              ? { run: d.run, resultados: d.resultados, casos: prev.casos.length > 0 ? prev.casos : d.casos }
              : prev,
          );
          setRuns((prev) => prev.map((r) => (r.run_id === d.run.run_id ? d.run : r)));
          setErrorDetalle(null);
        } catch (e) {
          console.error('[analisis] poll de corrida falló:', e);
          setErrorDetalle(`El poll falló (se reintenta en 5 s): ${e instanceof Error ? e.message : String(e)}`);
        } finally {
          setTick((t) => t + 1);
        }
      })();
    }, 5000);
    return () => clearTimeout(timer);
  }, [activaId, detalle, tick]);

  // Renovación de URLs firmadas: se re-pide el detalle y se reemplazan SOLO los casos.
  // `run` y `resultados` (con los veredictos ya aplicados en local) no se tocan, y el poll
  // de una corrida en curso tampoco los trae con URLs frescas (conserva `casos` a propósito).
  const refrescarFirmas = async () => {
    if (!activaId) return;
    const d = await fetchRun(activaId);
    setDetalle((prev) => (prev && prev.run.run_id === d.run.run_id ? { ...prev, casos: d.casos } : prev));
  };
  useRefrescoFirmas(detalle?.casos ?? SIN_CASOS, refrescarFirmas);

  const cancelar = async (run: RunResumen) => {
    if (!window.confirm(`¿Cancelar la corrida "${run.etiqueta}"? Las imágenes no analizadas quedan sin fila.`)) return;
    try {
      await cancelarRun(run.run_id);
      await recargarLista();
      if (activaId === run.run_id) setTick((t) => t + 1);
    } catch (e) {
      console.error('[analisis] cancelar corrida falló:', e);
      setErrorLista(e instanceof Error ? e.message : String(e));
    }
  };

  // Renombrar: la etiqueta guardada entra a la lista y al detalle al toque, sin esperar
  // al poll ni a la recarga. Tira si el backend falla (EtiquetaEditable muestra el error).
  const renombrar = async (runId: string, nueva: string) => {
    const guardada = await renombrarRun(runId, nueva);
    setRuns((prev) => prev.map((r) => (r.run_id === runId ? { ...r, etiqueta: guardada } : r)));
    setDetalle((prev) => (prev && prev.run.run_id === runId ? { ...prev, run: { ...prev.run, etiqueta: guardada } } : prev));
  };

  const onVeredicto = (imagenId: string, v: VeredictoHumano | null) =>
    setDetalle((prev) =>
      prev ? { ...prev, resultados: prev.resultados.map((r) => (r.imagen_id === imagenId ? { ...r, veredicto_humano: v } : r)) } : prev,
    );

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="font-semibold text-brand-black">Corridas</h3>
          <button type="button" onClick={() => void recargarLista()} disabled={cargandoLista} className={`${btnSecundario} py-1.5 px-3 text-xs`}>
            {cargandoLista ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
        <Aviso error={errorLista} className="mb-2" />
        {runs.length === 0 && !cargandoLista && <p className="text-sm text-gray-500">Sin corridas todavía. Lanzá una desde Casos.</p>}
        {runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  {['Etiqueta', 'Modelo', 'Effort', 'Fecha', 'Progreso', 'Fallidas', 'Costo', 'Estado', ''].map((h) => (
                    <th key={h} className="py-1 pr-3 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.run_id}
                    onClick={() => setActivaId(r.run_id)}
                    className={`cursor-pointer border-t border-gray-100 ${activaId === r.run_id ? 'bg-gray-50 font-medium' : 'hover:bg-gray-50'}`}
                  >
                    <td className="py-1.5 pr-3 text-brand-black">
                      <EtiquetaEditable valor={r.etiqueta} onGuardar={(nueva) => renombrar(r.run_id, nueva)} inputClassName="w-48 text-xs" />
                      {r.prompt_override && <span className="ml-1 text-[10px] text-gray-400">(prompt propio)</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-brand-black">{r.modelo}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.effort ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{formatearFecha(r.created_at)}</td>
                    <td className="py-1.5 pr-3 tabular-nums text-brand-black">
                      {r.hechas}/{r.total}
                    </td>
                    <td className={`py-1.5 pr-3 tabular-nums ${r.fallidas > 0 ? 'text-red-600' : 'text-gray-600'}`}>{r.fallidas}</td>
                    <td className="py-1.5 pr-3 tabular-nums text-gray-600">{formatearUsd(r.cost_usd)}</td>
                    <td className="py-1.5 pr-3">
                      <ChipStatus status={r.status} />
                    </td>
                    <td className="py-1.5">
                      {corridaEnCurso(r) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void cancelar(r);
                          }}
                          className="rounded-full border border-red-300 py-1 px-2.5 text-[11px] text-red-600 hover:bg-red-50"
                        >
                          Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cargandoDetalle && <p className="text-sm text-gray-500">Cargando corrida…</p>}
      <Aviso error={errorDetalle} />
      {detalle && (
        <DetalleCorrida
          detalle={detalle}
          config={config}
          onVeredicto={onVeredicto}
          onReutilizar={onReutilizar}
          onRenombrar={(nueva) => renombrar(detalle.run.run_id, nueva)}
        />
      )}
    </div>
  );
};

export default AnalisisCorridas;
